import { Table } from '@tanstack/react-table';
import _range from 'lodash/range';
import { useMemo } from 'react';

export function usePagination<TData>(table: Table<TData>) {
  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;

  /**
   * Generates an array of page numbers to be displayed in the pagination component.
   *
   * - If the total number of pages 5 or less, it simply returns a sequential array [1, 2, ..., pageCount].
   * - Otherwise, it constructs a pagination display with:
   *   - Always showing the first and last page.
   *   - Showing a range of 3 pages around the `currentPage`.
   *   - Adding "..." when there is a gap between displayed page numbers.
   *
   * Example Outputs:
   * - `pageCount = 4, currentPage = 2` → `[1, 2, 3, 4]`
   * - `pageCount = 10, currentPage = 1` → `[1, 2, 3, '...', 10]`
   * - `pageCount = 10, currentPage = 5` → `[1, '...', 4, 5, 6, '...', 10]`
   * - `pageCount = 100, currentPage = 29` → `[1, '...', 28, 29, 30, '...', 100]`
   */
  const generatedPageNumbers = useMemo(() => {
    // If the pageCount is less than or equal to 5, generate a range from 1 to the pageCount
    if (pageCount <= 5) return _range(1, pageCount + 1);
    const pages: (number | '...')[] = [];

    if (currentPage <= 3) {
      // When near the start, display first 3 pages, '...', last page
      pages.push(1, 2, 3, '...', pageCount);
    } else if (currentPage >= pageCount - 2) {
      // When near the end, display first page, '...', last 3 pages
      pages.push(1, '...', pageCount - 2, pageCount - 1, pageCount);
    } else {
      // When in the middle, keep the current page centered
      pages.push(1, '...', currentPage, '...', pageCount);
    }

    return pages;
  }, [pageCount, currentPage]);

  return { generatedPageNumbers };
}
